/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";

import { spacing, type ThemeType } from "../../../../global/emotion";

const tabsRootStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
};

const tabsListStyle: Interpolation<ThemeType> = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  backgroundColor: "transparent",
  color: theme.semanticColors.neutralsMedium,
  height: "auto",

  // Horizontal orientation (default) - bottom border
  "&[data-orientation='horizontal']": {
    flexDirection: "row",
    width: "100%",
    borderBottom: `2px solid ${theme.semanticColors.neutralsLowest}`,
    overflow: "auto",
  },

  // Vertical orientation - right border
  "&[data-orientation='vertical']": {
    flexDirection: "column",
    alignItems: "flex-start",
    width: "fit-content",
    minWidth: "180px",
    borderRight: `2px solid ${theme.semanticColors.neutralsLowest}`,
    paddingTop: theme.spacing.m,
    paddingBottom: theme.spacing.m,
  },
});

const tabsTriggerStyle: Interpolation<ThemeType> = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap",
  padding: theme.spacing.m,
  position: "relative",
  fontSize: theme.fontSizes.bodyM,
  fontWeight: theme.fontWeights.bodyM,
  lineHeight: theme.lineHeights.bodyM,
  transition: "color 0.25s ease-in-out, background-color 0.25s ease-in-out",
  cursor: "pointer",
  border: "none",

  "&:hover": {
    backgroundColor: theme.semanticColors.neutralsLowest,
    color: theme.semanticColors.neutralsHigher,
  },

  "&:focus-visible": {
    outline: `2px solid ${theme.semanticColors.primary}`,
    outlineOffset: "2px",
  },

  "&[data-state='active']": {
    color: theme.semanticColors.primary,
    backgroundColor: "transparent",

    // Horizontal: bottom border
    "&[data-orientation='horizontal']::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "3px",
      backgroundColor: theme.semanticColors.primary,
    },

    // Vertical: right border
    "&[data-orientation='vertical']::after": {
      content: '""',
      position: "absolute",
      top: theme.spacing.m,
      bottom: theme.spacing.m,
      right: 0,
      width: "2px",
      backgroundColor: theme.semanticColors.primary,
    },
  },

  "&:disabled": {
    pointerEvents: "none",
    opacity: 0.5,
  },

  // Orientation-specific styles
  "&[data-orientation='horizontal']": {
    minWidth: "fit-content",
    borderTopLeftRadius: theme.radius.s,
    borderTopRightRadius: theme.radius.s,
    marginBottom: -2, // Overlap the bottom border
  },

  "&[data-orientation='vertical']": {
    width: "100%",
    justifyContent: "flex-start",
    borderRadius: theme.radius.s,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    marginRight: -2, // Overlap the right border
  },
});

const tabsContentStyle: Interpolation<ThemeType> = (theme) => ({
  outline: "none",

  "&:focus-visible": {
    outline: `2px solid ${theme.semanticColors.primary}`,
    outlineOffset: "2px",
    borderRadius: theme.radius.m,
  },

  "&[data-state='active']": {
    animation: "fadeIn 150ms ease-out",
  },

  "@keyframes fadeIn": {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
});

const Tabs = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ ...props }, ref) => (
  <TabsPrimitive.Root ref={ref} css={tabsRootStyle} {...props} />
));
Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ ...props }, ref) => (
  <TabsPrimitive.List ref={ref} css={tabsListStyle} {...props} />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ ...props }, ref) => (
  <TabsPrimitive.Trigger ref={ref} css={tabsTriggerStyle} {...props} />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ ...props }, ref) => (
  <TabsPrimitive.Content ref={ref} css={tabsContentStyle} {...props} />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
